import React from 'react'
import { Button } from 'antd'
import styles from './EditorCommon.module.css'

// Executive Summary order
const EXECUTIVE_SUMMARY_ORDER = ['overall_assessment', 'key_metrics_to_track', 'weekly_structure', 'exercise', 'nutrition', 'sleep', 'next_steps']

// Protocol Infographic order
const PROTOCOL_INFOGRAPHIC_ORDER = ['summary', 'key_metrics', 'quick_wins', 'recommendations']

// Helper to get a display value for any type
const displayValue = (val) => {
  if (val === null || val === undefined) return <span className={styles.refValEmpty}>—</span>
  if (typeof val === 'boolean') return String(val)
  if (typeof val === 'object') return JSON.stringify(val).substring(0, 100) + (JSON.stringify(val).length > 100 ? '...' : '')
  return String(val)
}

// Ordered data viewer - renders keys in a specific order
const OrderedDataViewer = ({ data, orderedKeys, depth = 0 }) => {
  if (!data || typeof data !== 'object') {
    return <span>{displayValue(data)}</span>
  }

  if (Array.isArray(data)) {
    return (
      <div className={depth > 0 ? styles.refArrayNested : ''}>
        {data.length === 0 ? (
          <span className={styles.refValEmpty}>Empty list</span>
        ) : (
          data.map((item, i) => (
            <div key={i} className={depth > 0 ? styles.refArrayItemNested : styles.refArrayItem}>
              {typeof item === 'object' && item !== null ? (
                // Check if this is a recommendation (has title) or metric (has name)
                item.title !== undefined ? (
                  // Recommendation format
                  <div className={styles.refRecCard}>
                    {item.title && (
                      <div className={styles.refRecTitle}>
                        {String(item.title)}
                      </div>
                    )}
                    <div className={styles.refRecMeta}>
                      {item.priority && (
                        <span className={item.priority === 'critical' ? styles.refRecPriorityCritical : styles.refRecPriorityDefault}>
                          {String(item.priority)}
                        </span>
                      )}
                      {item.impact_score !== undefined && (
                        <span className={styles.refRecImpact}>
                          Impact: {item.impact_score}
                        </span>
                      )}
                      {item.timeframe && (
                        <span className={styles.refRecTimeframe}>
                          {String(item.timeframe)}
                        </span>
                      )}
                    </div>
                    {item.why_condensed && (
                      <div className={styles.refRecWhy}>
                        {String(item.why_condensed).substring(0, 100)}
                      </div>
                    )}
                    {item.best_action && (
                      <div className={styles.refRecAction}>
                        <span className={styles.refRecActionLabel}>Action: </span>{String(item.best_action).substring(0, 80)}
                      </div>
                    )}
                  </div>
                ) : item.name !== undefined ? (
                  // Metric format (key_metrics_to_track)
                  <div className={styles.refRecCard}>
                    <div className={styles.refRecTitle}>
                      {String(item.name)}
                    </div>
                    <div className={styles.refRecMeta}>
                      {item.current_value !== undefined && (
                        <>
                          <span className={styles.refRecImpact}>
                            Current: {item.current_value}{item.unit || ''}
                          </span>
                          {(item.direction === 'higher_is_better' || item.direction === 'up') ? ' ↑' : (item.direction === 'lower_is_better' || item.direction === 'down') ? ' ↓' : ''}
                        </>
                      )}
                      {item.target_value !== undefined && (
                        <span className={styles.refRecTimeframe}>
                          Target: {item.target_value}{item.unit || ''}
                        </span>
                      )}
                      {item.delta && (
                        <span className={styles.refRecTimeframe}>
                          {String(item.delta)}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  // Fallback: generic object display
                  <div className={styles.refRecCard}>
                    {Object.entries(item).filter(([k, v]) => v !== undefined && v !== null).map(([k, v]) => (
                      <div key={k} className={styles.refRecMeta}>
                        <span className={styles.refRecImpact}>{k.replace(/_/g, ' ')}: {String(v).substring(0, 40)}</span>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <span className={styles.refValText}>{String(item)}</span>
              )}
            </div>
          ))
        )}
      </div>
    )
  }

  // Get entries in the specified order, plus any extra keys at the end
  const entries = []
  if (orderedKeys) {
    orderedKeys.forEach(key => {
      if (data[key] !== undefined) entries.push([key, data[key]])
    })
    Object.entries(data).forEach(([key, value]) => {
      if (!orderedKeys.includes(key)) entries.push([key, value])
    })
  } else {
    Object.entries(data).forEach(([key, value]) => entries.push([key, value]))
  }

  return (
    <div className={depth > 0 ? styles.refSectionNested : ''}>
      {entries.map(([key, value]) => (
        <div key={key} className={depth > 0 ? styles.refFieldNested : styles.refField}>
          <div className={styles.refFieldLabel}>
            {key.replace(/_/g, ' ')}
          </div>
          <div className={styles.refFieldValue}>
            {typeof value === 'object' && value !== null ? (
              <OrderedDataViewer data={value} orderedKeys={orderedKeys} depth={depth + 1} />
            ) : (
              displayValue(value)
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const RefinementReview = ({ originalData, refinedData, onAccept, onDecline, title = 'Infographic', renderSection, orderedKeys }) => {
  // Use provided orderedKeys or default to Executive Summary order
  const effectiveOrderedKeys = orderedKeys || EXECUTIVE_SUMMARY_ORDER

  // If renderSection is provided, use it for each data half (same renderer = same order)
  if (renderSection) {
    return (
      <div className={styles.refinementReview}>
        <div className={styles.refinementHeader}>
          <span role="img" aria-label="sparkles" className={styles.refSparkleIcon}>✨</span>
          <span className={styles.refinementTitle}>AI Refinement Results — {title}</span>
        </div>

        <div className={styles.refinementColumns}>
          {/* Original */}
          <div className={styles.refinementColumn}>
            <div className={styles.refinementColumnHeader}>
              <span className={styles.refinementColumnLabel}>Original Content</span>
            </div>
            <div className={styles.refinementColumnBody}>
              {renderSection(originalData)}
            </div>
          </div>

          {/* Refined */}
          <div className={styles.refinementColumn}>
            <div className={styles.refinementColumnHeader}>
              <span className={styles.refinementColumnLabel}>Refined Content</span>
              <span className={styles.refinementAiBadge}>AI Suggested</span>
            </div>
            <div className={styles.refinementColumnBody}>
              {renderSection(refinedData)}
            </div>
          </div>
        </div>

        {/* Accept / Decline Buttons */}
        <div className={styles.refinementActions}>
          <Button size="large" onClick={onDecline} className={styles.refDeclineBtn}>
            Decline
          </Button>
          <Button type="primary" size="large" onClick={onAccept} className={styles.refAcceptBtn}>
            Accept Changes
          </Button>
        </div>
      </div>
    )
  }

  // Default: ordered key-value comparison (consistent order on both sides)
  return (
    <div className={styles.refinementReview}>
      <div className={styles.refinementHeader}>
        <span role="img" aria-label="sparkles" className={styles.refSparkleIcon}>✨</span>
        <span className={styles.refinementTitle}>AI Refinement Results — {title}</span>
      </div>

      <div className={styles.refinementColumns}>
        {/* Original */}
        <div className={styles.refinementColumn}>
          <div className={styles.refinementColumnHeader}>
            <span className={styles.refinementColumnLabel}>Original Content</span>
          </div>
          <div className={styles.refinementColumnBody}>
            <OrderedDataViewer data={originalData} orderedKeys={effectiveOrderedKeys} />
          </div>
        </div>

        {/* Refined */}
        <div className={styles.refinementColumn}>
          <div className={styles.refinementColumnHeader}>
            <span className={styles.refinementColumnLabel}>Refined Content</span>
            <span className={styles.refinementAiBadge}>AI Suggested</span>
          </div>
          <div className={styles.refinementColumnBody}>
            <OrderedDataViewer data={refinedData} orderedKeys={effectiveOrderedKeys} />
          </div>
        </div>
      </div>

      {/* Accept / Decline Buttons */}
      <div className={styles.refinementActions}>
        <Button size="large" onClick={onDecline} className={styles.refDeclineBtn}>
          Decline
        </Button>
        <Button type="primary" size="large" onClick={onAccept} className={styles.refAcceptBtn}>
          Accept Changes
        </Button>
      </div>
    </div>
  )
}

export default RefinementReview
